import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useGetMe, getGetMeQueryKey, useGetRecruiterMe, getGetRecruiterMeQueryKey, getCsrfToken, setCsrfToken, useDismissOnboardingModal, useMarkFeaturesViewed } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ShieldCheck, X, ChevronDown, Bookmark, LayoutDashboard, Briefcase, LogOut, User, UserPlus } from "lucide-react";
import { MobileWelcomeBubble } from "@/components/welcome-banner";
import { OtWSuccessSurveyModal } from "@/components/otw-success-survey-modal";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useHintDismissed, HINT_KEYS } from "@/hooks/use-hint-dismissed";
import { hasNewCapabilitiesForUser, isNewlyVerified } from "@/lib/capabilities";
import { OnboardingModal } from "@/components/invite/onboarding-modal";

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <rect x="1" y="5" width="20" height="2" rx="1" fill="currentColor" />
      <rect x="1" y="10" width="20" height="2" rx="1" fill="currentColor" />
      <rect x="1" y="15" width="20" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 3L17 17M17 3L3 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

interface AccountDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
}

function AccountDropdown({ trigger, children }: AccountDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <div onClick={() => setOpen((v) => !v)} className="cursor-pointer">
        {trigger}
      </div>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-48 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl shadow-black/40 z-[55] overflow-hidden py-1"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownLink({ href, icon, children }: { href: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors font-medium"
    >
      <span className="text-zinc-500">{icon}</span>
      {children}
    </Link>
  );
}

function DropdownButton({ onClick, icon, children, danger }: { onClick: () => void; icon: React.ReactNode; children: React.ReactNode; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm transition-colors font-medium text-left ${
        danger
          ? "text-red-400 hover:text-red-300 hover:bg-red-500/5"
          : "text-zinc-300 hover:text-white hover:bg-white/5"
      }`}
    >
      <span className={danger ? "text-red-500/60" : "text-zinc-500"}>{icon}</span>
      {children}
    </button>
  );
}

function DropdownDivider() {
  return <div className="my-1 border-t border-white/5" />;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { data: me } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false },
  });
  const { data: recruiter } = useGetRecruiterMe({
    query: { queryKey: getGetRecruiterMeQueryKey(), retry: false },
  });
  const [location] = useLocation();
  const search = useSearch();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [surveyDismissed, setSurveyDismissed] = useState(false);
  const [ggRevealed, setGgRevealed] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const loginHref =
    /^\/profile\/\d+$/.test(location) || /^\/jobs\/\d+$/.test(location)
      ? `/login?returnTo=${encodeURIComponent(location)}`
      : "/login";

  const isLoggedIn = Boolean(me);
  const isRecruiter = Boolean(recruiter);
  const hasNewCapabilities =
    isLoggedIn &&
    !isRecruiter &&
    hasNewCapabilitiesForUser(me?.featuresLastViewedAt);
  const isVerifiedAlumni = me?.verified === true;

  // Post-verification onboarding modal. Rendered from the layout so it appears on
  // the first page load regardless of where the member lands, not only /dashboard.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const dismissOnboardingMutation = useDismissOnboardingModal();
  const { mutate: markFeaturesViewed } = useMarkFeaturesViewed();
  // Keyed by user id (not a bare boolean) so an account switch within the same
  // SPA session re-evaluates eligibility instead of being permanently blocked.
  const onboardingHandledUserRef = useRef<number | null>(null);
  const meId = me?.id;
  const meVerified = me?.verified;
  const meOnboardingModalDismissed = me?.onboardingModalDismissed;
  const meProfileCreatedAt = me?.profileCreatedAt;

  useEffect(() => {
    if (isRecruiter) return;
    if (meId == null || meVerified !== true || meOnboardingModalDismissed) return;
    if (onboardingHandledUserRef.current === meId) return;
    onboardingHandledUserRef.current = meId;
    setOnboardingOpen(true);
    // Newly verified members effectively see every capability at once here, so
    // mark the Features page as viewed now — no nav dot or "New" flags for them.
    if (isNewlyVerified(meProfileCreatedAt)) {
      markFeaturesViewed(undefined, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        },
      });
    }
  }, [
    isRecruiter,
    meId,
    meVerified,
    meOnboardingModalDismissed,
    meProfileCreatedAt,
    markFeaturesViewed,
    queryClient,
  ]);

  const handleDismissOnboarding = () => {
    setOnboardingOpen(false);
    dismissOnboardingMutation.mutate(undefined, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      },
    });
  };

  const isActive = (href: string) => {
    if (href === "/recruiter/dashboard" && location.startsWith("/jobs/manage")) {
      return true;
    }
    if (href.includes("?")) {
      const [hrefPath, hrefQuery] = href.split("?");
      const hrefParams = new URLSearchParams(hrefQuery);
      const currentParams = new URLSearchParams(search);
      return (
        (location === hrefPath || (hrefPath === "/" && location === "")) &&
        hrefParams.get("tab") === currentParams.get("tab")
      );
    }
    if (href === "/") {
      const tab = new URLSearchParams(search).get("tab");
      return (location === "/" || location === "") && (!tab || tab === "directory");
    }
    return location === href || location.startsWith(href + "/");
  };

  const navLinkClass = (path: string) =>
    `transition-colors ${
      isActive(path)
        ? "text-white underline underline-offset-4 decoration-primary/60"
        : "text-zinc-400 hover:text-white"
    }`;

  const drawerLinkClass = (path: string) =>
    `block w-full py-3 px-4 text-base font-medium rounded-md transition-colors ${
      isActive(path)
        ? "text-white bg-white/5 border-l-2 border-primary"
        : "text-zinc-300 hover:text-white hover:bg-white/5"
    }`;

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "x-csrf-token": getCsrfToken() ?? "" },
      });
    } catch {
    }
    setCsrfToken(null);
    queryClient.clear();
    setDrawerOpen(false);
    window.location.assign("/");
  };

  const handleRecruiterLogout = async () => {
    try {
      await fetch("/api/recruiters/logout", {
        method: "POST",
        headers: { "x-csrf-token": getCsrfToken() ?? "" },
      });
    } catch {
    }
    setCsrfToken(null);
    queryClient.clear();
    setDrawerOpen(false);
    window.location.assign("/");
  };

  const handleDismissWelcome = async () => {
    setWelcomeDismissed(true);
    try {
      await fetch("/api/profiles/me/dismiss-welcome-banner", {
        method: "POST",
        headers: { "x-csrf-token": getCsrfToken() ?? "" },
      });
    } catch {
    }
    queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  };

  const platformLinkRef = useRef<HTMLAnchorElement>(null);

  const [opportunitiesHintDismissed] = useHintDismissed(HINT_KEYS.opportunitiesManageAlerts);
  const [communitiesHintDismissed] = useHintDismissed(HINT_KEYS.communitiesManageAlerts);

  const isVerifiedAlumniViewer = me?.hasVerifiedRole === true;
  const onOpportunitiesRoute =
    (location === "/" || location === "") &&
    (new URLSearchParams(search).get("tab") ?? "talent") === "opportunities";
  const onCommunitiesRoute =
    location === "/communities-events" || location.startsWith("/communities-events/");
  const manageAlertsHintActive =
    (onOpportunitiesRoute && isVerifiedAlumniViewer && !opportunitiesHintDismissed) ||
    (onCommunitiesRoute && isVerifiedAlumniViewer && !communitiesHintDismissed);

  const showWelcomeBubble =
    me?.verified === true &&
    me?.welcomeBannerDismissed === false &&
    !welcomeDismissed &&
    !manageAlertsHintActive;

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  const alumniDisplayName = me?.displayName ?? me?.linkedinName ?? null;
  const alumniAvatar = me?.avatar ?? me?.linkedinAvatar ?? null;
  const alumniInitials = alumniDisplayName
    ? alumniDisplayName.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <div className="min-h-[100dvh] flex flex-col w-full bg-background text-foreground selection:bg-primary selection:text-white">
      <header className="sticky top-0 z-50 w-full border-b border-white/5 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-6xl flex h-16 items-center justify-between px-4">
          <Link href="/" className="flex items-center group">
            <span className="font-bold text-base tracking-[0.2em] uppercase text-zinc-100 group-hover:text-white transition-colors">
              The Carry Network
            </span>
          </Link>

          <nav className="flex items-center gap-2 text-sm font-medium">
            <div className="hidden md:flex items-center gap-6">
              <Link href="/" className={navLinkClass("/")}>
                Directory
              </Link>
              <Link href="/?tab=opportunities" className={navLinkClass("/?tab=opportunities")}>
                Opportunities
              </Link>
              {!isRecruiter && (
                <>
                  {me?.hasVerifiedRole === true && (
                    <Link href="/communities-events" className={navLinkClass("/communities-events")}>
                      Community
                    </Link>
                  )}
                  {isLoggedIn && (
                    <div className="relative">
                      <Link href="/platform" className={navLinkClass("/platform")}>
                        Platform
                      </Link>
                      {hasNewCapabilities && (
                        <span className="absolute -top-1 -right-2 h-2 w-2 rounded-full bg-primary pointer-events-none" aria-hidden="true" />
                      )}
                      <AnimatePresence>
                        {showWelcomeBubble && (
                          <motion.div
                            key="welcome-desktop"
                            initial={prefersReducedMotion ? false : { opacity: 0, y: -6, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.97 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="hidden md:block absolute top-full left-1/2 -translate-x-1/2 mt-2 z-[55] w-[280px]"
                          >
                            <div
                              className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                              style={{
                                borderLeft: "8px solid transparent",
                                borderRight: "8px solid transparent",
                                borderBottom: "8px solid #3b82f6",
                              }}
                            />
                            <div
                              className="relative rounded-lg border bg-zinc-950 p-4 pr-9 shadow-xl shadow-black/50 font-sans"
                              style={{ borderColor: "#3b82f6" }}
                            >
                              <button
                                onClick={handleDismissWelcome}
                                aria-label="Dismiss welcome message"
                                className="absolute top-2 right-2 p-1 rounded text-zinc-400 hover:text-white transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-sm leading-relaxed text-zinc-200">
                                Welcome to The Carry Network. Please leave any feedback in here. Thanks for joining!
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </>
              )}
              <Button asChild variant="outline" size="sm" className="text-zinc-100 border-zinc-600 hover:border-primary hover:text-white">
                <Link href="/jobs/new">Post Opportunity</Link>
              </Button>
            </div>
            <div className="hidden md:block w-px h-4 bg-zinc-800 mx-2" />

            {/* Desktop account area */}
            {isRecruiter ? (
              <div className="hidden md:block">
                <AccountDropdown
                  trigger={
                    <button className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
                      <span className="text-sm font-semibold max-w-[120px] truncate">{recruiter?.companyName}</span>
                      <ChevronDown className="w-3.5 h-3.5 shrink-0 text-zinc-500" />
                    </button>
                  }
                >
                  <DropdownLink href="/recruiter/dashboard" icon={<Briefcase className="w-3.5 h-3.5" />}>
                    My Job Posts
                  </DropdownLink>
                  <DropdownLink href="/recruiter/saved" icon={<Bookmark className="w-3.5 h-3.5" />}>
                    Saved Profiles
                  </DropdownLink>
                  <DropdownDivider />
                  <DropdownButton onClick={handleRecruiterLogout} icon={<LogOut className="w-3.5 h-3.5" />} danger>
                    Log Out
                  </DropdownButton>
                </AccountDropdown>
              </div>
            ) : isLoggedIn ? (
              <div className="hidden md:block">
                <AccountDropdown
                  trigger={
                    <button className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors px-1 py-1 rounded-lg hover:bg-white/5">
                      <Avatar className="w-7 h-7 rounded-md border border-white/10">
                        <AvatarImage src={alumniAvatar ?? undefined} alt={alumniDisplayName ?? "Account"} className="object-cover rounded-md" />
                        <AvatarFallback className="bg-zinc-800 text-zinc-300 text-xs font-bold rounded-md">{alumniInitials}</AvatarFallback>
                      </Avatar>
                      {alumniDisplayName && (
                        <span className="text-sm font-semibold max-w-[100px] truncate hidden lg:block">{alumniDisplayName.split(" ")[0]}</span>
                      )}
                      <ChevronDown className="w-3.5 h-3.5 shrink-0 text-zinc-500" />
                    </button>
                  }
                >
                  {me?.profileId != null && isVerifiedAlumni && (
                    <>
                      <DropdownLink href={`/profile/${me.profileId}`} icon={<User className="w-3.5 h-3.5" />}>
                        My Profile
                      </DropdownLink>
                      <Link
                        href="/dashboard#invite"
                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Invite Alumni
                      </Link>
                      <DropdownDivider />
                    </>
                  )}
                  <DropdownLink href="/dashboard" icon={<LayoutDashboard className="w-3.5 h-3.5" />}>
                    My Dashboard
                  </DropdownLink>
                  {isVerifiedAlumni && (
                    <DropdownLink href="/saved" icon={<Bookmark className="w-3.5 h-3.5" />}>
                      Saved Profiles
                    </DropdownLink>
                  )}
                  {isVerifiedAlumni && (
                    <DropdownLink href="/my-jobs" icon={<Briefcase className="w-3.5 h-3.5" />}>
                      My Job Posts
                    </DropdownLink>
                  )}
                  <DropdownDivider />
                  <DropdownButton onClick={handleLogout} icon={<LogOut className="w-3.5 h-3.5" />} danger>
                    Log Out
                  </DropdownButton>
                </AccountDropdown>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  href={loginHref}
                  className="inline-flex items-center text-zinc-300 hover:text-white transition-colors font-semibold"
                >
                  Sign In
                </Link>
                <Link
                  href={loginHref}
                  className="inline-flex items-center px-4 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold text-sm transition-colors"
                >
                  Join
                </Link>
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex items-center justify-center text-zinc-300 hover:text-white transition-colors p-1"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
            >
              <MenuIcon />
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer panel */}
      <div
        id="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`fixed top-0 right-0 z-[70] h-full w-72 overflow-hidden bg-zinc-950 border-l border-white/5 flex flex-col md:hidden transform transition-transform duration-300 ease-in-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/5">
          <span className="font-bold text-sm tracking-[0.2em] uppercase text-zinc-100">
            Menu
          </span>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-zinc-400 hover:text-white transition-colors p-1"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="flex flex-col gap-1 p-4 flex-1 overflow-y-auto overflow-x-hidden">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-4 mb-2">
            Explore
          </p>
          <Link href="/" className={drawerLinkClass("/")}>
            Directory
          </Link>
          <Link href="/?tab=opportunities" className={drawerLinkClass("/?tab=opportunities")}>
            Opportunities
          </Link>
          {!isRecruiter && (
            <>
              {me?.hasVerifiedRole === true && (
                <Link href="/communities-events" className={drawerLinkClass("/communities-events")}>
                  Community
                </Link>
              )}
              {isLoggedIn && (
                <div className="relative">
                  <Link href="/platform" ref={platformLinkRef} className={drawerLinkClass("/platform")}>
                    Platform
                  </Link>
                  {hasNewCapabilities && (
                    <span className="absolute top-3 right-4 h-2 w-2 rounded-full bg-primary pointer-events-none" aria-hidden="true" />
                  )}
                </div>
              )}
            </>
          )}
          <Link href="/jobs/new" className={drawerLinkClass("/jobs/new")}>
            Post Opportunity
          </Link>

          <div className="my-4 border-t border-white/5" />

          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-4 mb-2">
            Account
          </p>

          {isRecruiter ? (
            <>
              <Link href="/recruiter/dashboard" className={drawerLinkClass("/recruiter/dashboard")}>
                My Job Posts
              </Link>
              <Link href="/recruiter/saved" className={drawerLinkClass("/recruiter/saved")}>
                Saved Profiles
              </Link>
              <button
                onClick={handleRecruiterLogout}
                className="cursor-pointer block w-full py-3 px-4 text-base font-medium rounded-md text-left text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                Log Out
              </button>
            </>
          ) : isLoggedIn ? (
            <>
              {me?.profileId != null && isVerifiedAlumni && (
                <>
                  <Link href={`/profile/${me.profileId}`} className={drawerLinkClass(`/profile/${me.profileId}`)}>
                    My Profile
                  </Link>
                  <Link
                    href="/dashboard#invite"
                    className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold text-sm transition-colors mt-1"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Invite Alumni
                  </Link>
                </>
              )}
              <Link href="/dashboard" className={drawerLinkClass("/dashboard")}>
                My Dashboard
              </Link>
              {isVerifiedAlumni && (
                <Link href="/saved" className={drawerLinkClass("/saved")}>
                  Saved Profiles
                </Link>
              )}
              {isVerifiedAlumni && (
                <Link href="/my-jobs" className={drawerLinkClass("/my-jobs")}>
                  My Job Posts
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="cursor-pointer block w-full py-3 px-4 text-base font-medium rounded-md text-left text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                Log Out
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <Link
                href={loginHref}
                className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 font-semibold text-sm transition-colors"
              >
                Sign In
              </Link>
              <Link
                href={loginHref}
                className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold text-sm transition-colors"
              >
                Join
              </Link>
            </div>
          )}
        </nav>
      </div>

      {/* Mobile welcome tooltip — rendered outside the drawer to escape overflow-hidden */}
      <MobileWelcomeBubble
        show={showWelcomeBubble}
        onDismiss={handleDismissWelcome}
        feedbackRef={platformLinkRef}
      />

      {/* Open-to-Work success survey modal — verified alumni only */}
      {me && !isRecruiter && isVerifiedAlumni && (
        <OtWSuccessSurveyModal
          profileId={me.profileId}
          open={me.showSuccessSurvey === true && !surveyDismissed}
          onDismiss={() => setSurveyDismissed(true)}
        />
      )}

      <main className="flex-1 flex flex-col">{children}</main>

      <footer className="border-t border-white/5 py-12 mt-20">
        <div className="container mx-auto max-w-6xl px-4 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-zinc-500">
          <p>© 2026 The Carry Network. Not affiliated with or endorsed by Riot Games, Inc.</p>
          <div className="flex items-center gap-4">
            <Link href="/about" className="hover:text-zinc-300 transition-colors">
              Privacy Policy
            </Link>
            <Link href="/about" className="hover:text-zinc-300 transition-colors">
              Terms of Service
            </Link>
            {me?.role === "admin" && (
              <Link
                href="/admin"
                className="flex items-center gap-1 text-zinc-700 hover:text-primary transition-colors"
                aria-label="Admin dashboard"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
              </Link>
            )}
            <button
              type="button"
              onClick={() => setGgRevealed((v) => !v)}
              onBlur={() => setGgRevealed(false)}
              className="group relative select-none cursor-pointer bg-transparent border-0 p-0 m-0 text-xs leading-none focus:outline-none"
              aria-label="GG"
              aria-expanded={ggRevealed}
            >
              <span
                className={`${ggRevealed ? "text-zinc-600" : "text-zinc-800"} group-hover:text-zinc-600 transition-colors duration-300`}
              >
                GG
              </span>
              <span
                className={`absolute right-0 bottom-full mb-2 w-max max-w-[80vw] text-zinc-500 transition-opacity duration-500 pointer-events-none whitespace-normal md:whitespace-nowrap text-right ${
                  ggRevealed ? "opacity-100" : "opacity-0"
                } group-hover:opacity-100`}
              >
                Thank you to all Rioters for their earnest effort towards making it better to be a Player.
              </span>
            </button>
          </div>
        </div>
      </footer>

      {!isRecruiter && me?.verified === true && (
        <OnboardingModal
          open={onboardingOpen}
          createdAt={me.profileCreatedAt}
          onDismiss={handleDismissOnboarding}
          dismissing={dismissOnboardingMutation.isPending}
        />
      )}
    </div>
  );
}
